<template>
    <div>
        <div class="mb-1 rounded-md border border-slate-200 bg-slate-50 p-1">
            <div class="flex flex-wrap gap-1">
                <button
                    v-for="item in formattingButtons"
                    :key="item.label"
                    type="button"
                    class="email-toolbar-button"
                    :class="{ 'is-active': isFormattingActive(item.active) }"
                    :title="item.title"
                    @click="runFormattingCommand(item.command)"
                >
                    <AppIcon v-if="item.icon" :name="item.icon" class="h-3.5 w-3.5" />
                    <span v-else :class="item.class">{{ item.label }}</span>
                </button>

                <span class="mx-1 my-1 w-px bg-slate-200"></span>

                <button
                    type="button"
                    class="email-toolbar-button"
                    :class="{ 'is-active': editor?.isActive('link') }"
                    title="Add or edit link"
                    @click="openLinkEditor"
                >
                    <AppIcon name="link" class="h-3.5 w-3.5" />
                </button>
                <button
                    type="button"
                    class="email-toolbar-button"
                    :disabled="!editor?.isActive('link')"
                    title="Remove link"
                    @click="removeLink"
                >
                    <AppIcon name="unlink" class="h-3.5 w-3.5" />
                </button>

                <span class="mx-1 my-1 w-px bg-slate-200"></span>

                <div v-if="showEmoji" class="relative flex items-center">
                    <button
                        type="button"
                        class="email-toolbar-button"
                        title="Insert emoji"
                        @click="emojiMenuOpen = !emojiMenuOpen"
                    >
                        <AppIcon name="face-grin" class="h-3.5 w-3.5" />
                    </button>
                    <div
                        v-if="emojiMenuOpen"
                        class="absolute left-0 z-20 mt-1 grid w-52 grid-cols-8 gap-1 rounded-md border border-slate-200 bg-white p-2 shadow-lg"
                    >
                        <button
                            v-for="emoji in commonEmojis"
                            :key="emoji"
                            type="button"
                            class="rounded p-1 text-base hover:bg-slate-100"
                            @click="insertEmoji(emoji)"
                        >
                            {{ emoji }}
                        </button>
                    </div>
                </div>

                <button type="button" class="email-toolbar-button" title="Upload image" @click="triggerImageUpload">
                    <AppIcon name="image" class="h-3.5 w-3.5" />
                </button>
                <input
                    ref="imageInput"
                    type="file"
                    class="hidden"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    @change="onImageSelect"
                />

                <button type="button" class="email-toolbar-button" title="Insert image URL" @click="openImageUrlEditor">
                    <span class="relative inline-flex h-4 w-4 items-center justify-center">
                        <AppIcon name="image" class="h-3.5 w-3.5" />
                        <AppIcon
                            name="link"
                            class="absolute -bottom-0.5 -right-1 h-2.5 w-2.5 rounded-full bg-slate-50 p-px"
                        />
                    </span>
                </button>

                <select
                    class="email-toolbar-select"
                    :disabled="!hasSelectedImage"
                    :value="currentImageSize"
                    title="Image size"
                    @mousedown="captureImageSelectionForToolbar"
                    @focus="captureImageSelectionForToolbar"
                    @blur="imageToolbarInteracting = false"
                    @change="setImageSize($event.target.value)"
                >
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                </select>

                <button
                    type="button"
                    class="email-toolbar-button"
                    :class="{ 'is-active': isAlignmentActive('left') }"
                    title="Align left"
                    @click="setContentAlignment('left')"
                >
                    <AppIcon name="align-left" class="h-3.5 w-3.5" />
                </button>
                <button
                    type="button"
                    class="email-toolbar-button"
                    :class="{ 'is-active': isAlignmentActive('center') }"
                    title="Align center"
                    @click="setContentAlignment('center')"
                >
                    <AppIcon name="align-center" class="h-3.5 w-3.5" />
                </button>
                <button
                    type="button"
                    class="email-toolbar-button"
                    :class="{ 'is-active': isAlignmentActive('right') }"
                    title="Align right"
                    @click="setContentAlignment('right')"
                >
                    <AppIcon name="align-right" class="h-3.5 w-3.5" />
                </button>

                <span class="mx-1 my-1 w-px bg-slate-200"></span>

                <button
                    type="button"
                    class="email-toolbar-button"
                    title="Paste from clipboard"
                    @click="pasteFromClipboard"
                >
                    <AppIcon name="clipboard-list" class="h-3.5 w-3.5" />
                </button>
            </div>

            <div v-if="linkEditorOpen" class="mt-2 flex flex-col gap-2 sm:flex-row">
                <input
                    ref="linkInput"
                    v-model="linkUrl"
                    type="url"
                    class="form-input min-w-0 flex-1 text-sm"
                    placeholder="https://example.com or mailto:name@example.com"
                    @keydown.enter.prevent="applyLink"
                    @keydown.esc.prevent="closeLinkEditor"
                />
                <div class="flex gap-2">
                    <Button type="button" size="sm" @click="applyLink">Apply</Button>
                    <Button type="button" variant="outline" size="sm" @click="closeLinkEditor">Cancel</Button>
                </div>
            </div>

            <div v-if="imageUrlEditorOpen" class="mt-2 flex flex-col gap-2 sm:flex-row">
                <input
                    ref="imageUrlInput"
                    v-model="imageUrl"
                    type="url"
                    class="form-input min-w-0 flex-1 text-sm"
                    placeholder="https://example.com/logo.png"
                    @keydown.enter.prevent="applyImageUrl"
                    @keydown.esc.prevent="closeImageUrlEditor"
                />
                <div class="flex gap-2">
                    <Button type="button" size="sm" @click="applyImageUrl">Insert</Button>
                    <Button type="button" variant="outline" size="sm" @click="closeImageUrlEditor">Cancel</Button>
                </div>
            </div>
        </div>

        <EditorContent :editor="editor" class="email-editor" :class="editorClass" />

        <div v-if="notice" class="mt-2 rounded-md bg-amber-50 p-2 text-sm text-amber-700 ring-1 ring-amber-600/10">
            {{ notice }}
        </div>
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { Fragment } from '@tiptap/pm/model'
import { Button } from '@/components/ui/button'

const MAX_UPLOAD_TOTAL_SIZE = 3 * 1024 * 1024
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
const IMAGE_ALIGNMENTS = ['left', 'center', 'right']
const IMAGE_SIZES = ['small', 'medium', 'large']

function imageStyle(align = 'center', size = 'large') {
    const alignments = {
        left: 'display:block;margin:8px auto 8px 0;max-width:100%;height:auto;',
        center: 'display:block;margin:8px auto;max-width:100%;height:auto;',
        right: 'display:block;margin:8px 0 8px auto;max-width:100%;height:auto;',
    }

    const sizes = {
        small: 'width:240px;',
        medium: 'width:480px;',
        large: 'width:100%;',
    }

    return `${alignments[align] || alignments.center}${sizes[size] || sizes.large}`
}

function parseImageAlignment(element) {
    const dataAlign = element.getAttribute('data-align')

    if (IMAGE_ALIGNMENTS.includes(dataAlign)) {
        return dataAlign
    }

    const { marginLeft, marginRight } = element.style

    if (marginLeft === 'auto' && marginRight === 'auto') {
        return 'center'
    }

    if (marginRight === 'auto') {
        return 'left'
    }

    if (marginLeft === 'auto') {
        return 'right'
    }

    return 'center'
}

function parseImageSize(element) {
    const dataSize = element.getAttribute('data-size')

    if (IMAGE_SIZES.includes(dataSize)) {
        return dataSize
    }

    const width = element.style.width

    if (/100%/.test(width)) {
        return 'large'
    }

    const pixelWidth = Number.parseFloat(width)

    if (Number.isFinite(pixelWidth)) {
        if (pixelWidth <= 240) {
            return 'small'
        }

        if (pixelWidth <= 480) {
            return 'medium'
        }
    }

    return 'large'
}

const EmailImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            align: {
                default: 'center',
                parseHTML: (element) => parseImageAlignment(element),
                renderHTML: (attributes) => {
                    const align = attributes.align || 'center'
                    return {
                        'data-align': align,
                        style: imageStyle(align, attributes.size),
                    }
                },
            },
            size: {
                default: 'large',
                parseHTML: (element) => parseImageSize(element),
                renderHTML: (attributes) => ({
                    'data-size': attributes.size || 'large',
                }),
            },
            contentId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-content-id'),
                renderHTML: (attributes) => {
                    if (!attributes.contentId) {
                        return {}
                    }

                    return {
                        'data-content-id': attributes.contentId,
                    }
                },
            },
        }
    },
})

export default {
    name: 'EmailRichTextEditor',

    components: {
        Button,
        EditorContent,
    },

    props: {
        modelValue: { type: String, default: '' },
        inlineImages: { type: Array, default: () => [] },
        additionalUploadSize: { type: Number, default: 0 },
        imageMode: {
            type: String,
            default: 'attachments',
            validator: (value) => ['attachments', 'embedded'].includes(value),
        },
        editorClass: { type: String, default: '' },
        showEmoji: { type: Boolean, default: true },
        allowBlockquote: { type: Boolean, default: true },
    },

    emits: ['update:modelValue', 'update:inlineImages', 'error'],

    data() {
        return {
            editor: null,
            localInlineImages: [...this.inlineImages],
            editorSelectionVersion: 0,
            selectedImagePos: null,
            imageToolbarInteracting: false,
            linkEditorOpen: false,
            linkUrl: '',
            imageUrlEditorOpen: false,
            imageUrl: '',
            emojiMenuOpen: false,
            notice: '',
            commonEmojis: [
                '\uD83D\uDE00',
                '\uD83D\uDE03',
                '\uD83D\uDE04',
                '\uD83D\uDE0A',
                '\uD83D\uDE0D',
                '\uD83D\uDE09',
                '\uD83D\uDE4C',
                '\uD83D\uDE4F',
                '\uD83D\uDC4D',
                '\uD83D\uDC4F',
                '\uD83D\uDD25',
                '\uD83C\uDF89',
                '\u2728',
                '\u2764\uFE0F',
                '\uD83D\uDC40',
                '\uD83D\uDCAF',
            ],
        }
    },

    computed: {
        formattingButtons() {
            return [
                { label: 'B', title: 'Bold', active: 'bold', command: 'bold', class: 'font-bold' },
                { label: 'I', title: 'Italic', active: 'italic', command: 'italic', class: 'italic' },
                { label: 'U', title: 'Underline', active: 'underline', command: 'underline', class: 'underline' },
                ...(this.allowBlockquote
                    ? [
                          {
                              label: 'Quote',
                              title: 'Block quote',
                              active: 'blockquote',
                              command: 'blockquote',
                              icon: 'quote-left',
                          },
                      ]
                    : []),
                {
                    label: 'Bullet',
                    title: 'Bullet list',
                    active: 'bulletList',
                    command: 'bulletList',
                    icon: 'list-ul',
                },
                {
                    label: 'Numbered',
                    title: 'Numbered list',
                    active: 'orderedList',
                    command: 'orderedList',
                    icon: 'list-ol',
                },
                {
                    label: 'Clear',
                    title: 'Clear formatting',
                    active: null,
                    command: 'clear',
                    icon: 'eraser',
                },
            ]
        },

        currentImageSize() {
            const selectionVersion = this.editorSelectionVersion
            const image = selectionVersion >= 0 ? this.getActiveSelectedImage() : null

            return image?.attrs?.size || 'large'
        },

        hasSelectedImage() {
            const selectionVersion = this.editorSelectionVersion

            return selectionVersion >= 0 && !!this.getActiveSelectedImage()
        },
    },

    watch: {
        modelValue(newValue) {
            if (!this.editor || newValue === this.editor.getHTML()) {
                return
            }

            this.editor.commands.setContent(newValue || '', false)
        },

        inlineImages(newValue) {
            this.localInlineImages = [...(newValue || [])]
        },
    },

    mounted() {
        this.editor = new Editor({
            extensions: [
                StarterKit.configure({
                    bulletList: true,
                    listItem: true,
                    orderedList: true,
                    blockquote: this.allowBlockquote,
                }),
                Link.configure({
                    openOnClick: false,
                    autolink: true,
                    linkOnPaste: true,
                    protocols: ['mailto', 'tel'],
                    HTMLAttributes: {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                    },
                    isAllowedUri: (href) => this.isAllowedLinkUrl(href),
                    shouldAutoLink: (href) => this.isAllowedLinkUrl(href),
                }),
                EmailImage,
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                }),
                Underline,
            ],
            content: this.modelValue,
            onUpdate: ({ editor }) => {
                const body = editor.getHTML()
                this.$emit('update:modelValue', body)
                this.syncInlineImagesWithBody(body)
                this.updateSelectedImageState()
                this.editorSelectionVersion += 1
            },
            onSelectionUpdate: () => {
                this.updateSelectedImageState()
                this.editorSelectionVersion += 1
            },
        })
    },

    beforeUnmount() {
        this.editor?.destroy()
    },

    methods: {
        getHTML() {
            return this.editor?.getHTML() || this.modelValue || ''
        },

        getText() {
            return this.editor?.getText() || ''
        },

        setContent(content = '') {
            if (this.editor) {
                this.editor.commands.setContent(content || '', false)
            }

            this.$emit('update:modelValue', content || '')
        },

        focus() {
            this.editor?.commands.focus()
        },

        insertContent(content) {
            if (this.editor) {
                this.editor.chain().focus().insertContent(content).run()
                this.$emit('update:modelValue', this.editor.getHTML())
            }
        },

        isFormattingActive(markOrNode) {
            if (!markOrNode || !this.editor) {
                return false
            }

            return this.editor.isActive(markOrNode)
        },

        runFormattingCommand(command) {
            if (!this.editor) {
                return
            }

            if (['blockquote', 'bulletList', 'orderedList'].includes(command) && this.runLineScopedBlockCommand(command)) {
                return
            }

            const chain = this.editor.chain().focus()
            const commands = {
                bold: () => chain.toggleBold().run(),
                italic: () => chain.toggleItalic().run(),
                underline: () => chain.toggleUnderline().run(),
                blockquote: () => chain.toggleBlockquote().run(),
                bulletList: () => chain.toggleBulletList().run(),
                orderedList: () => chain.toggleOrderedList().run(),
                clear: () => chain.unsetAllMarks().clearNodes().run(),
            }

            commands[command]?.()
        },

        runLineScopedBlockCommand(command) {
            const { state, view, schema } = this.editor
            const { selection } = state
            const { $from, $to } = selection
            const parent = $from.parent

            if ($from.depth !== $to.depth || $from.parent !== $to.parent || parent.type.name !== 'paragraph') {
                return false
            }

            const hasHardBreaks = parent.content.content.some((node) => node.type.name === 'hardBreak')

            if (!hasHardBreaks) {
                return false
            }

            const paragraphPos = $from.before($from.depth)
            const paragraphStart = paragraphPos + 1
            const lines = this.getParagraphLines(parent, paragraphStart)
            const selectedIndexes = this.getSelectedLineIndexes(lines, selection)

            if (!selectedIndexes.length) {
                return false
            }

            const firstSelected = selectedIndexes[0]
            const lastSelected = selectedIndexes[selectedIndexes.length - 1]
            const replacement = [
                ...lines.slice(0, firstSelected).map((line) => this.createParagraphFromLine(schema, parent, line)),
                this.createBlockNodeFromLines(command, schema, parent, lines.slice(firstSelected, lastSelected + 1)),
                ...lines.slice(lastSelected + 1).map((line) => this.createParagraphFromLine(schema, parent, line)),
            ].filter(Boolean)

            const tr = state.tr.replaceWith(
                paragraphPos,
                paragraphPos + parent.nodeSize,
                Fragment.fromArray(replacement),
            )

            view.dispatch(tr.scrollIntoView())
            this.editor.commands.focus()

            return true
        },

        getParagraphLines(paragraph, paragraphStart) {
            const lines = [{ content: [], start: paragraphStart, end: paragraphStart }]
            let offset = 0

            paragraph.forEach((node) => {
                const nodeStart = paragraphStart + offset
                const nodeEnd = nodeStart + node.nodeSize
                const currentLine = lines[lines.length - 1]

                if (node.type.name === 'hardBreak') {
                    currentLine.end = nodeStart
                    lines.push({ content: [], start: nodeEnd, end: nodeEnd })
                } else {
                    currentLine.content.push(node)
                    currentLine.end = nodeEnd
                }

                offset += node.nodeSize
            })

            return lines
        },

        getSelectedLineIndexes(lines, selection) {
            if (selection.empty) {
                const index = lines.findIndex((line) => selection.from >= line.start && selection.from <= line.end)
                return index === -1 ? [] : [index]
            }

            return lines.reduce((indexes, line, index) => {
                if (line.end > selection.from && line.start < selection.to) {
                    indexes.push(index)
                }

                return indexes
            }, [])
        },

        createParagraphFromLine(schema, sourceParagraph, line) {
            return schema.nodes.paragraph.create(
                sourceParagraph.attrs,
                line.content.length ? Fragment.fromArray(line.content) : null,
            )
        },

        createBlockNodeFromLines(command, schema, sourceParagraph, lines) {
            const paragraphs = lines.map((line) => this.createParagraphFromLine(schema, sourceParagraph, line))

            if (command === 'blockquote') {
                return schema.nodes.blockquote.create(null, Fragment.fromArray(paragraphs))
            }

            const listType = command === 'orderedList' ? schema.nodes.orderedList : schema.nodes.bulletList
            const listItems = paragraphs.map((paragraph) => {
                return schema.nodes.listItem.create(null, paragraph)
            })

            return listType.create(null, Fragment.fromArray(listItems))
        },

        openLinkEditor() {
            if (!this.editor) {
                return
            }

            this.closeImageUrlEditor()
            this.linkUrl = this.editor.getAttributes('link')?.href || ''
            this.linkEditorOpen = true
            this.$nextTick(() => this.$refs.linkInput?.focus())
        },

        closeLinkEditor() {
            this.linkEditorOpen = false
            this.linkUrl = ''
        },

        applyLink() {
            if (!this.editor) {
                return
            }

            const href = this.linkUrl.trim()
            if (!href) {
                this.removeLink()
                this.closeLinkEditor()
                return
            }

            if (!this.isAllowedLinkUrl(href)) {
                this.showNotice('Links must start with http://, https://, mailto:, or tel:.')
                return
            }

            const { from, to } = this.editor.state.selection
            const linkAttrs = {
                href,
                target: '_blank',
                rel: 'noopener noreferrer',
            }

            if (from === to) {
                this.editor
                    .chain()
                    .focus()
                    .insertContent({
                        type: 'text',
                        text: href,
                        marks: [{ type: 'link', attrs: linkAttrs }],
                    })
                    .run()
            } else {
                this.editor.chain().focus().extendMarkRange('link').setLink(linkAttrs).run()
            }

            this.closeLinkEditor()
        },

        removeLink() {
            if (!this.editor) {
                return
            }

            this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
        },

        isAllowedLinkUrl(url = '') {
            return /^(https?:\/\/|mailto:|tel:)/i.test(String(url || '').trim())
        },

        insertEmoji(emoji) {
            this.insertContent(emoji)
            this.emojiMenuOpen = false
        },

        triggerImageUpload() {
            this.$refs.imageInput?.click()
        },

        async onImageSelect(event) {
            const file = event.target.files?.[0]
            event.target.value = ''

            if (!file) {
                return
            }

            await this.insertUploadedImage(file)
        },

        openImageUrlEditor() {
            this.closeLinkEditor()
            this.imageUrl = ''
            this.imageUrlEditorOpen = true
            this.$nextTick(() => this.$refs.imageUrlInput?.focus())
        },

        closeImageUrlEditor() {
            this.imageUrlEditorOpen = false
            this.imageUrl = ''
        },

        applyImageUrl() {
            const src = this.imageUrl.trim()

            if (!/^https?:\/\//i.test(src)) {
                this.showNotice('Image URLs must start with http:// or https://.')
                return
            }

            this.insertImage({ src, align: 'center', size: 'large' })
            this.closeImageUrlEditor()
        },

        async pasteFromClipboard() {
            if (!navigator.clipboard?.read) {
                this.focus()
                this.showNotice('Clipboard access is blocked here. Use Ctrl+V or Cmd+V in the editor.')
                return
            }

            try {
                const items = await navigator.clipboard.read()
                let inserted = false

                for (const item of items) {
                    if (item.types.includes('text/html')) {
                        const blob = await item.getType('text/html')
                        this.insertContent(await blob.text())
                        inserted = true
                        break
                    }

                    if (item.types.includes('text/plain')) {
                        const blob = await item.getType('text/plain')
                        this.insertContent((await blob.text()).replace(/\n/g, '<br>'))
                        inserted = true
                        break
                    }
                }

                if (!inserted) {
                    this.showNotice('No text or HTML was found on the clipboard.')
                }
            } catch (error) {
                this.focus()
                this.showNotice('Clipboard access was not allowed. Use Ctrl+V or Cmd+V in the editor.')
            }
        },

        async insertUploadedImage(file) {
            if (!this.editor) {
                return
            }

            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                this.showImageError('Inline images must be PNG, JPG, GIF, or WEBP files.')
                return
            }

            const currentTotal = this.getUploadTotalSize()
            let processedFile = file
            const imageLimitMessage =
                'Email uploads can be up to 3 MB total. Remove an attachment or image, or choose a smaller file.'

            if (currentTotal >= MAX_UPLOAD_TOTAL_SIZE) {
                this.showImageError(imageLimitMessage)
                return
            }

            const shouldCompress =
                file.type !== 'image/gif' &&
                (file.size > 1024 * 1024 || currentTotal + file.size > MAX_UPLOAD_TOTAL_SIZE)

            if (shouldCompress) {
                processedFile = await this.compressImage(file, MAX_UPLOAD_TOTAL_SIZE - currentTotal)

                if (!processedFile) {
                    this.showImageError(imageLimitMessage)
                    return
                }
            }

            if (currentTotal + processedFile.size > MAX_UPLOAD_TOTAL_SIZE) {
                this.showImageError(imageLimitMessage)
                return
            }

            try {
                const content = await this.fileToBase64(processedFile)
                const dataUrl = `data:${processedFile.type};base64,${content}`

                if (this.imageMode === 'attachments') {
                    const contentId = `sf-inline-${this.createId()}`
                    const inlineImage = {
                        filename: processedFile.name,
                        content_type: processedFile.type,
                        content,
                        size: processedFile.size,
                        original_size: file.size,
                        content_id: contentId,
                        disposition: 'inline',
                    }

                    this.setInlineImages([...this.localInlineImages, inlineImage])
                    this.insertImage({
                        src: dataUrl,
                        alt: processedFile.name,
                        align: 'center',
                        size: 'large',
                        contentId,
                    })
                    return
                }

                this.insertImage({
                    src: dataUrl,
                    alt: processedFile.name,
                    align: 'center',
                    size: 'large',
                })
            } catch (error) {
                console.error('Inline image processing failed:', error)
                this.showImageError(`We couldn't upload "${file.name}". Please try a different image.`)
            }
        },

        insertImage(attrs) {
            this.editor?.chain().focus().setImage(attrs).run()
        },

        setContentAlignment(align) {
            if (!this.editor) {
                return
            }

            if (this.getActiveSelectedImage()) {
                this.updateSelectedImageAttributes({ align })
                return
            }

            this.editor.chain().focus().setTextAlign(align).run()
        },

        setImageSize(size) {
            if (!['small', 'medium', 'large'].includes(size)) {
                return
            }

            this.updateSelectedImageAttributes({ size })
        },

        isAlignmentActive(align) {
            if (!this.editor) {
                return false
            }

            const image = this.getActiveSelectedImage()

            if (image) {
                return image.attrs?.align === align
            }

            return this.editor.isActive({ textAlign: align })
        },

        getSelectedImage() {
            if (!this.editor) {
                return null
            }

            const { selection } = this.editor.state

            if (selection.node?.type?.name === 'image') {
                return {
                    pos: selection.from,
                    attrs: { ...selection.node.attrs },
                }
            }

            let selectedImage = null

            this.editor.state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
                if (node.type.name === 'image') {
                    selectedImage = {
                        pos,
                        attrs: { ...node.attrs },
                    }

                    return false
                }

                return true
            })

            return selectedImage
        },

        getStoredSelectedImage() {
            if (!this.editor || this.selectedImagePos === null) {
                return null
            }

            const node = this.editor.state.doc.nodeAt(this.selectedImagePos)

            if (node?.type?.name !== 'image') {
                return null
            }

            return {
                pos: this.selectedImagePos,
                attrs: { ...node.attrs },
            }
        },

        getActiveSelectedImage() {
            return this.getSelectedImage() || this.getStoredSelectedImage()
        },

        updateSelectedImageState() {
            const selectedImage = this.getSelectedImage()

            if (selectedImage) {
                this.selectedImagePos = selectedImage.pos
                return
            }

            if (this.imageToolbarInteracting && this.getStoredSelectedImage()) {
                return
            }

            this.selectedImagePos = null
        },

        captureImageSelectionForToolbar() {
            const selectedImage = this.getSelectedImage() || this.getStoredSelectedImage()

            if (!selectedImage) {
                this.imageToolbarInteracting = false
                return
            }

            this.selectedImagePos = selectedImage.pos
            this.imageToolbarInteracting = true
        },

        updateSelectedImageAttributes(attrs) {
            if (!this.editor) {
                return
            }

            const selectedImage = this.getActiveSelectedImage()

            if (!selectedImage) {
                return
            }

            this.editor
                .chain()
                .focus()
                .setNodeSelection(selectedImage.pos)
                .updateAttributes('image', attrs)
                .run()

            this.updateSelectedImageState()
            this.imageToolbarInteracting = false
            this.editorSelectionVersion += 1
        },

        getCurrentInlineImages() {
            return this.getInlineImagesForBody(this.getHTML())
        },

        getInlineImagesForBody(body) {
            if (this.imageMode !== 'attachments') {
                return []
            }

            const bodyHtml = body || ''
            const referencedContentIds = new Set()

            if (bodyHtml && typeof DOMParser !== 'undefined') {
                const parser = new DOMParser()
                const document = parser.parseFromString(bodyHtml, 'text/html')

                document.querySelectorAll('img').forEach((image) => {
                    const contentId = image.getAttribute('data-content-id')
                    const src = image.getAttribute('src') || ''

                    if (contentId) {
                        referencedContentIds.add(contentId)
                    }

                    if (src.startsWith('cid:')) {
                        referencedContentIds.add(src.slice(4))
                    }
                })
            }

            return this.localInlineImages.filter((image) => {
                return (
                    referencedContentIds.has(image.content_id) ||
                    bodyHtml.includes(`cid:${image.content_id}`) ||
                    bodyHtml.includes(`data-content-id="${image.content_id}"`) ||
                    bodyHtml.includes(`data-content-id='${image.content_id}'`)
                )
            })
        },

        syncInlineImagesWithBody(body) {
            if (this.imageMode !== 'attachments') {
                return
            }

            this.setInlineImages(this.getInlineImagesForBody(body))
        },

        setInlineImages(images) {
            this.localInlineImages = images
            this.$emit('update:inlineImages', images)
        },

        getUploadFileSize(file) {
            if (file.content) {
                return file.content.length * 0.75
            }

            return file.size || 0
        },

        getUploadOriginalFileSize(file) {
            return file.original_size || this.getUploadFileSize(file)
        },

        getInlineImageTotalSize() {
            return this.getCurrentInlineImages().reduce((total, file) => {
                return total + this.getUploadFileSize(file)
            }, 0)
        },

        getInlineImageOriginalTotalSize() {
            return this.getCurrentInlineImages().reduce((total, file) => {
                return total + this.getUploadOriginalFileSize(file)
            }, 0)
        },

        getUploadTotalSize() {
            if (this.imageMode === 'attachments') {
                return this.additionalUploadSize + this.getInlineImageTotalSize()
            }

            return this.getEmbeddedImageTotalSize()
        },

        getEmbeddedImageTotalSize() {
            const body = this.getHTML()
            const matches = body.match(/src=["']data:image\/[^;]+;base64,([^"']+)["']/gi) || []

            return matches.reduce((total, match) => {
                const content = match.replace(/^src=["']data:image\/[^;]+;base64,/i, '').replace(/["']$/, '')

                return total + content.length * 0.75
            }, 0)
        },

        createId() {
            if (window.crypto?.randomUUID) {
                return window.crypto.randomUUID()
            }

            return `${Date.now()}-${Math.random().toString(16).slice(2)}`
        },

        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.readAsDataURL(file)
                reader.onload = () => resolve(reader.result.split(',')[1])
                reader.onerror = (error) => reject(error)
            })
        },

        compressImage(file, maxSize) {
            return new Promise((resolve, reject) => {
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')
                const img = new window.Image()
                const objectUrl = URL.createObjectURL(file)

                img.onload = () => {
                    let width = img.width
                    let height = img.height
                    const maxDim = 1920

                    if (width > maxDim || height > maxDim) {
                        if (width > height) {
                            height = (height * maxDim) / width
                            width = maxDim
                        } else {
                            width = (width * maxDim) / height
                            height = maxDim
                        }
                    }

                    canvas.width = width
                    canvas.height = height
                    ctx.drawImage(img, 0, 0, width, height)
                    URL.revokeObjectURL(objectUrl)

                    const tryQuality = (quality) => {
                        canvas.toBlob(
                            (blob) => {
                                if (!blob) {
                                    reject(new Error('Compression failed'))
                                    return
                                }

                                if (blob.size <= maxSize || quality <= 0.1) {
                                    resolve(
                                        blob.size <= maxSize
                                            ? new File([blob], file.name, { type: file.type, lastModified: Date.now() })
                                            : null,
                                    )
                                    return
                                }

                                tryQuality(quality - 0.1)
                            },
                            file.type,
                            quality,
                        )
                    }

                    tryQuality(0.8)
                }

                img.onerror = () => {
                    URL.revokeObjectURL(objectUrl)
                    reject(new Error('Image load failed'))
                }

                img.src = objectUrl
            })
        },

        showImageError(message) {
            this.$emit('error', message)
            this.showNotice(message)
        },

        showNotice(message) {
            this.notice = message
        },
    },
}
</script>

<style scoped>
.email-toolbar-button {
    display: inline-flex;
    min-height: 1.875rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1;
    color: rgb(51 65 85);
    transition:
        background-color 0.15s ease,
        color 0.15s ease;
}

.email-toolbar-button svg {
    display: block;
}

.email-toolbar-button:hover:not(:disabled),
.email-toolbar-button.is-active {
    background-color: rgb(226 232 240);
    color: rgb(15 23 42);
}

.email-toolbar-button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.email-toolbar-select {
    min-height: 1.875rem;
    border-radius: 0.25rem;
    border: 1px solid rgb(203 213 225);
    background-color: #fff;
    padding: 0.25rem 1.75rem 0.25rem 0.5rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: rgb(51 65 85);
}

.email-toolbar-select:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.email-editor :deep(.ProseMirror) {
    height: 380px;
    width: 100%;
    overflow-y: auto;
    border: 1px solid rgb(203 213 225);
    border-radius: 0.375rem;
    padding: 0.5rem 0.75rem;
    background-color: #fff;
    line-height: 1.5;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
}

.email-editor :deep(.ProseMirror:focus),
.email-editor :deep(.ProseMirror:focus-visible) {
    outline: none !important;
    border-color: rgb(99 102 241);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
}

.email-editor :deep(.ProseMirror a) {
    color: rgb(79 70 229);
    text-decoration: underline;
}

.email-editor :deep(.ProseMirror blockquote) {
    margin: 0.75rem 0;
    border-left: 3px solid rgb(203 213 225);
    padding-left: 0.75rem;
    color: rgb(71 85 105);
}

.email-editor :deep(.ProseMirror ul),
.email-editor :deep(.ProseMirror ol) {
    margin: 0.75rem 0;
    padding-left: 1.5rem;
}

.email-editor :deep(.ProseMirror ul) {
    list-style-type: disc;
}

.email-editor :deep(.ProseMirror ol) {
    list-style-type: decimal;
}

.email-editor :deep(.ProseMirror img) {
    max-width: 100%;
    height: auto;
}

.email-editor :deep(.ProseMirror img.ProseMirror-selectednode) {
    outline: 2px solid rgb(99 102 241);
    outline-offset: 2px;
}
</style>
